export type PainPoint = 'documentation' | 'patient-questions' | 'scheduling' | 'intake' | 'billing';
export type SetupDifficulty = 'easy' | 'medium' | 'hard';
export type PricingTier = 'free' | 'low' | 'medium' | 'enterprise';

export interface Tool {
  id: string;
  name: string;
  category: 'scribe' | 'intake' | 'chatbot' | 'scheduling' | 'billing' | 'specialty';
  subcategory?: string;
  description: string;
  website: string;
  hipaaCompliant: boolean;
  pricing: string;
  pricingTier: PricingTier;
  bestFor: string;
  keyFeatures: string[];
  keywords: string[];
  painPoints: PainPoint[];
  setupDifficulty: SetupDifficulty;
  setupTime: string;
  specialties?: string[]; // which specialties this is good for
  /** Loom (or other https) walkthrough video, from the DB catalog */
  videoUrl?: string;
  /** Free-form setup notes entered in the admin panel */
  guideNotes?: string;
  /** 'catalog' = added via the admin panel (DB row); undefined = built-in */
  source?: 'catalog';
  /** DB row id — needed for admin edit/delete (slug is the Tool id) */
  catalogId?: string;
}

export const tools: Tool[] = [
  // === SCRIBES ===
  {
    id: 'freed-ai',
    name: 'Freed AI',
    category: 'scribe',
    description: 'AI medical scribe that listens to patient encounters and auto-generates clinical notes. Adapts to each clinician\'s style.',
    website: 'https://www.getfreed.ai/',
    hipaaCompliant: true,
    pricing: 'Freemium; Paid Plans ~$99/Month.',
    pricingTier: 'low',
    bestFor: 'Clinics With 2-50 Clinicians',
    keyFeatures: [
      'No Patient Recording Storage',
      'Works On Any Device',
      'Sets Up In Minutes, No IT Required',
      'Customizable Templates'
    ],
    keywords: ['scribe', 'documentation', 'notes', 'charting', 'dictation', 'voice'],
    painPoints: ['documentation'],
    setupDifficulty: 'easy',
    setupTime: '10-15 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'scribeberry',
    name: 'Scribeberry',
    category: 'scribe',
    description: 'AI scribe with 99.9% accuracy that auto-generates structured notes for any EMR. Also includes patient intake agents.',
    website: 'https://www.scribeberry.com/',
    hipaaCompliant: true,
    pricing: 'Subscription-Based.',
    pricingTier: 'medium',
    bestFor: 'Practices Wanting High Accuracy',
    keyFeatures: [
      '99.9% Accuracy Claim',
      'Works With All EMRs',
      'Patient Intake Agents Included',
      'Auto-Fill Forms'
    ],
    keywords: ['scribe', 'documentation', 'notes', 'charting', 'emr', 'intake'],
    painPoints: ['documentation', 'intake'],
    setupDifficulty: 'easy',
    setupTime: '15-20 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'doximity-scribe',
    name: 'Doximity Scribe',
    category: 'scribe',
    description: 'Free AI clinical documentation tool from Doximity. Processes recordings in real-time and immediately discards audio.',
    website: 'https://blog.doximity.com/articles/meet-doximity-scribe',
    hipaaCompliant: true,
    pricing: 'Free For All U.S. Physicians, NPs, PAs, Medical Students.',
    pricingTier: 'free',
    bestFor: 'Any Physician With A Doximity Account',
    keyFeatures: [
      'Completely Free',
      'No Audio Storage',
      'Real-Time Processing',
      'BAA Included For All Doximity Users'
    ],
    keywords: ['scribe', 'documentation', 'notes', 'charting', 'free', 'doximity'],
    painPoints: ['documentation'],
    setupDifficulty: 'easy',
    setupTime: '5-10 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },

  // === INTAKE ===
  {
    id: 'intakeq',
    name: 'IntakeQ',
    category: 'intake',
    description: 'Healthcare-first intake forms with conditional logic, e-signatures, and EHR integration. HIPAA compliant by design.',
    website: 'https://intakeq.com/',
    hipaaCompliant: true,
    pricing: 'Starting ~$29/Month.',
    pricingTier: 'low',
    bestFor: 'Any Healthcare Practice Needing Custom Intake Workflows',
    keyFeatures: [
      'Drag-And-Drop Form Builder',
      'Conditional Logic',
      'E-Signatures',
      'EHR Integration',
      'Automated Reminders'
    ],
    keywords: ['intake', 'forms', 'patient', 'registration', 'onboarding', 'questionnaire'],
    painPoints: ['intake'],
    setupDifficulty: 'medium',
    setupTime: '30-45 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'jotform',
    name: 'Jotform (Healthcare)',
    category: 'intake',
    description: 'AI form generator + chatbot builder with HIPAA compliance option. 10,000+ templates available.',
    website: 'https://www.jotform.com/',
    hipaaCompliant: true,
    pricing: 'Free Tier; Paid From ~$34/Month; 50% Healthcare Discount.',
    pricingTier: 'low',
    bestFor: 'Practices Wanting AI-Generated Forms',
    keyFeatures: [
      'AI Form Generation From Descriptions',
      '10,000+ Templates',
      'Chatbot Agents',
      'HIPAA Compliance On Higher Tiers'
    ],
    keywords: ['intake', 'forms', 'patient', 'ai', 'templates', 'chatbot'],
    painPoints: ['intake', 'patient-questions'],
    setupDifficulty: 'easy',
    setupTime: '15-30 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'infermedica',
    name: 'Infermedica Intake',
    category: 'intake',
    description: 'AI-powered symptom collection and pre-visit intake. Intelligent algorithms ensure comprehensive symptom capture.',
    website: 'https://infermedica.com/solutions/intake',
    hipaaCompliant: true,
    pricing: 'Enterprise Pricing.',
    pricingTier: 'enterprise',
    bestFor: 'Practices Wanting Clinical-Grade AI Triage',
    keyFeatures: [
      'Intelligent Symptom Collection',
      'Reduces Visit Times (20 Min To 12.5 Min)',
      'Clinical Decision Support',
      'Pre-Visit Triage'
    ],
    keywords: ['intake', 'triage', 'symptoms', 'clinical', 'ai', 'pre-visit'],
    painPoints: ['intake'],
    setupDifficulty: 'hard',
    setupTime: '10,000-20,000 Minutes',
    specialties: ['general']
  },

  // === CHATBOTS ===
  {
    id: 'kommunicate',
    name: 'Kommunicate',
    category: 'chatbot',
    description: 'AI chatbot builder that can be trained on your own documents. Includes bot-to-human handoff.',
    website: 'https://www.kommunicate.io/',
    hipaaCompliant: true,
    pricing: 'Starting ~$100/Month.',
    pricingTier: 'medium',
    bestFor: 'Practices Wanting Custom FAQ Bots',
    keyFeatures: [
      'Train On Your Documents',
      'Custom Branding',
      'Bot-To-Human Handoff',
      'Live Agent Dashboard'
    ],
    keywords: ['chatbot', 'faq', 'support', 'documents', 'train', 'custom'],
    painPoints: ['patient-questions'],
    setupDifficulty: 'medium',
    setupTime: '60-120 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'bastiongpt',
    name: 'BastionGPT',
    category: 'chatbot',
    description: 'HIPAA-compliant ChatGPT for healthcare. Secure version where data is not accessible to OpenAI or used for training.',
    website: 'https://bastiongpt.com/',
    hipaaCompliant: true,
    pricing: 'Subscription-Based.',
    pricingTier: 'medium',
    bestFor: 'Practices Wanting ChatGPT Capabilities With PHI Safety',
    keyFeatures: [
      'Exceeds HIPAA Requirements',
      'Data Not Used For OpenAI Training',
      'Full GPT-4 Capabilities',
      'Document Analysis'
    ],
    keywords: ['chatbot', 'chatgpt', 'gpt', 'hipaa', 'secure', 'ai'],
    painPoints: ['patient-questions', 'documentation'],
    setupDifficulty: 'easy',
    setupTime: '10-15 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },

  // === SCHEDULING ===
  {
    id: 'nexhealth',
    name: 'NexHealth',
    category: 'scheduling',
    description: 'Patient experience platform with smart scheduling, automated reminders, and reviews management.',
    website: 'https://www.nexhealth.com/',
    hipaaCompliant: true,
    pricing: 'Contact For Pricing.',
    pricingTier: 'medium',
    bestFor: 'Dental, Medical, And Specialty Practices',
    keyFeatures: [
      'Online Booking',
      'Automated Reminders',
      'Waitlist Management',
      'EHR Sync',
      'Review Management'
    ],
    keywords: ['scheduling', 'appointments', 'booking', 'reminders', 'waitlist'],
    painPoints: ['scheduling'],
    setupDifficulty: 'medium',
    setupTime: '60-120 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },
  {
    id: 'emitrr',
    name: 'Emitrr',
    category: 'scheduling',
    description: 'AI-powered scheduling with VoIP, automated reminders, and patient communication all-in-one.',
    website: 'https://emitrr.com/',
    hipaaCompliant: true,
    pricing: 'Affordable For Small Practices.',
    pricingTier: 'low',
    bestFor: 'Small To Medium Practices Wanting All-In-One Communication',
    keyFeatures: [
      'AI Chatbot',
      'Appointment Scheduling',
      'Two-Way Texting',
      'VoIP Calling',
      'Automated Reminders'
    ],
    keywords: ['scheduling', 'appointments', 'communication', 'texting', 'voip', 'reminders'],
    painPoints: ['scheduling', 'patient-questions'],
    setupDifficulty: 'easy',
    setupTime: '30-45 Minutes',
    specialties: ['plastic-surgery', 'dermatology', 'orthopedics', 'general']
  },

  // === SPECIALTY: PLASTIC SURGERY ===
  {
    id: 'touchmd',
    name: 'TouchMD',
    category: 'specialty',
    subcategory: 'Plastic Surgery',
    description: 'Consultation app with before/after simulation and patient education for plastic surgeons and aestheticians.',
    website: 'https://www.touchmd.com/',
    hipaaCompliant: true,
    pricing: 'Contact For Pricing.',
    pricingTier: 'medium',
    bestFor: 'Plastic Surgeons, Cosmetic Practices',
    keyFeatures: [
      'iPad-Based Consultations',
      'Procedure Visualization',
      'Before/After Simulation',
      'Consent Management',
      'Patient Education'
    ],
    keywords: ['plastic surgery', 'cosmetic', 'consultation', 'simulation', 'before after'],
    painPoints: ['patient-questions'],
    setupDifficulty: 'medium',
    setupTime: '60-120 Minutes',
    specialties: ['plastic-surgery']
  },
  {
    id: 'aesthetix-crm',
    name: 'Aesthetix CRM',
    category: 'specialty',
    subcategory: 'Plastic Surgery',
    description: 'CRM for plastic surgeons with AI-powered lead management, intake automation, and marketing tools.',
    website: 'https://aesthetixcrm.com/',
    hipaaCompliant: true,
    pricing: 'Contact For Pricing.',
    pricingTier: 'medium',
    bestFor: 'Cosmetic Surgery, Dermatology, Medical Spas',
    keyFeatures: [
      'AI Lead Management',
      'Intake Automation',
      'Marketing Tools',
      'EMR Integrations (ModMed, Nextech, 4D EMR)'
    ],
    keywords: ['plastic surgery', 'cosmetic', 'crm', 'leads', 'marketing', 'dermatology'],
    painPoints: ['intake', 'scheduling'],
    setupDifficulty: 'medium',
    setupTime: '120-240 Minutes',
    specialties: ['plastic-surgery', 'dermatology']
  },

  // === SPECIALTY: DERMATOLOGY ===
  {
    id: 'miiskin',
    name: 'Miiskin',
    category: 'specialty',
    subcategory: 'Dermatology',
    description: 'AI skin lesion analysis with patient photo documentation and change tracking over time.',
    website: 'https://miiskin.com/',
    hipaaCompliant: true,
    pricing: 'Contact For Pricing.',
    pricingTier: 'medium',
    bestFor: 'Dermatology Practices',
    keyFeatures: [
      'AI Lesion Detection',
      'Patient Photo Documentation',
      'Change Tracking Over Time',
      'Teledermatology Support'
    ],
    keywords: ['dermatology', 'skin', 'lesion', 'mole', 'imaging', 'analysis'],
    painPoints: ['documentation'],
    setupDifficulty: 'medium',
    setupTime: '60-120 Minutes',
    specialties: ['dermatology']
  },
  {
    id: 'fotofinder',
    name: 'FotoFinder',
    category: 'specialty',
    subcategory: 'Dermatology',
    description: 'Clinical-grade total body mapping and AI lesion detection. Moleanalyzer pro validated at Heidelberg University.',
    website: 'https://www.fotofinder.de/en/',
    hipaaCompliant: true,
    pricing: 'Enterprise Pricing.',
    pricingTier: 'enterprise',
    bestFor: 'Dermatology Practices Needing Clinical-Grade Imaging',
    keyFeatures: [
      'Total Body Mapping',
      'Moleanalyzer Pro AI',
      'Dermoscopy Integration',
      'Validated Performance Comparable To Dermatologists'
    ],
    keywords: ['dermatology', 'skin', 'lesion', 'mole', 'imaging', 'body mapping', 'dermoscopy'],
    painPoints: ['documentation'],
    setupDifficulty: 'hard',
    setupTime: '10,000-20,000 Minutes',
    specialties: ['dermatology']
  }
];

export const categories = [
  { id: 'scribe', label: 'AI Scribes', description: 'Reduce Documentation Time' },
  { id: 'intake', label: 'Patient Intake', description: 'Smart Forms & Questionnaires' },
  { id: 'chatbot', label: 'Chatbots', description: 'Train on your documents' },
  { id: 'scheduling', label: 'Scheduling', description: 'Appointments & Reminders' },
  { id: 'billing', label: 'Billing & Coding', description: 'Claims & Coding Tools' },
  { id: 'specialty', label: 'Specialty Tools', description: 'Specialty Specific Tools' }
];

/** Category / subcategory text for UI badges (tool tabs, cards, chat). */
export function getCategoryDisplayLabel(tool: Pick<Tool, 'category' | 'subcategory'>): string {
  if (tool.category === 'specialty') {
    if (tool.subcategory) return tool.subcategory;
    return categories.find((c) => c.id === 'specialty')?.label ?? 'Specialty Tools';
  }
  return categories.find((c) => c.id === tool.category)?.label ?? capitalizeCategoryId(tool.category);
}

function capitalizeCategoryId(slug: string): string {
  return slug
    .replace(/-/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export const painPointLabels: Record<PainPoint, string> = {
  'documentation': 'I spend too much time on documentation',
  'patient-questions': 'Patients have questions I can\'t always answer quickly',
  'scheduling': 'Scheduling and appointments are a mess',
  'intake': 'Patient intake is tedious or paper-based',
  'billing': 'Billing and coding takes forever'
};

export const specialtyLabels: Record<string, string> = {
  'plastic-surgery': 'Plastic Surgery',
  'dermatology': 'Dermatology',
  'orthopedics': 'Orthopedics',
  'general': 'General Practice / Other'
};

export function searchTools(query: string, filters?: { hipaaOnly?: boolean; category?: string }): Tool[] {
  const lowerQuery = query.toLowerCase();

  let results = tools.filter(tool => {
    const matchesQuery =
      tool.name.toLowerCase().includes(lowerQuery) ||
      tool.description.toLowerCase().includes(lowerQuery) ||
      tool.keywords.some(kw => kw.includes(lowerQuery)) ||
      tool.category.includes(lowerQuery) ||
      (tool.subcategory?.toLowerCase().includes(lowerQuery) ?? false);

    return matchesQuery;
  });

  if (filters?.hipaaOnly) {
    results = results.filter(tool => tool.hipaaCompliant);
  }

  if (filters?.category) {
    results = results.filter(tool => tool.category === filters.category);
  }

  return results;
}

// --- Dynamic catalog registry -------------------------------------------
// Tools added through the admin panel live in the database and are fetched
// at runtime by CatalogContext. Registering them here lets every existing
// getToolById consumer (tabs, chat cards, toolbox) resolve them without
// threading context through each callsite. On id collision the DB row wins,
// so admin edits can override a built-in entry.
let catalogTools: Tool[] = [];

export function setCatalogTools(dynamic: Tool[]): void {
  catalogTools = dynamic;
}

export function getAllTools(): Tool[] {
  if (catalogTools.length === 0) return tools;
  const overridden = new Set(catalogTools.map(t => t.id));
  return [...catalogTools, ...tools.filter(t => !overridden.has(t.id))];
}

export function getToolById(id: string): Tool | undefined {
  return catalogTools.find(tool => tool.id === id) ?? tools.find(tool => tool.id === id);
}

export function getToolsByCategory(category: string): Tool[] {
  return tools.filter(tool => tool.category === category);
}

export interface WizardFilters {
  painPoint?: PainPoint;
  specialty?: string;
  pricingPreference?: 'free' | 'low' | 'any';
  easySetup?: boolean;
}

export function getRecommendedTools(filters: WizardFilters): Tool[] {
  let results = [...tools];

  // Filter by pain point
  if (filters.painPoint) {
    results = results.filter(tool => tool.painPoints.includes(filters.painPoint!));
  }

  // Filter by specialty
  if (filters.specialty && filters.specialty !== 'general') {
    results = results.filter(tool =>
      tool.specialties?.includes(filters.specialty!) ||
      tool.specialties?.includes('general')
    );
  }

  // Filter by pricing
  if (filters.pricingPreference === 'free') {
    results = results.filter(tool => tool.pricingTier === 'free');
  } else if (filters.pricingPreference === 'low') {
    results = results.filter(tool => tool.pricingTier === 'free' || tool.pricingTier === 'low');
  }

  // Filter by setup difficulty
  if (filters.easySetup) {
    results = results.filter(tool => tool.setupDifficulty === 'easy');
  }

  // Sort: HIPAA compliant first, then by setup difficulty
  results.sort((a, b) => {
    if (a.hipaaCompliant && !b.hipaaCompliant) return -1;
    if (!a.hipaaCompliant && b.hipaaCompliant) return 1;

    const difficultyOrder = { easy: 0, medium: 1, hard: 2 };
    return difficultyOrder[a.setupDifficulty] - difficultyOrder[b.setupDifficulty];
  });

  return results.slice(0, 3); // Return top 3
}
